/* eslint-disable react/no-unescaped-entities */
import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { toast } from "sonner";
import { Plus, X, ArrowRight, ArrowLeft } from "lucide-react";

const INTENTION_SUGGESTIONS = [
  "Get steady — no rush",
  "Reconnect with my tamariki",
  "Find safe housing",
  "Stay connected to my recovery",
  "Reclaim my voice",
  "Build income that's mine",
  "Reconnect with my whakapapa",
  "Heal — at my own pace",
];

export default function Onboarding() {
  const { user, updateProfile } = useAuth();
  const nav = useNavigate();
  const [step, setStep] = useState(0);
  const [intentions, setIntentions] = useState(user?.intentions || []);
  const [contacts, setContacts] = useState(user?.trusted_contacts?.length ? user.trusted_contacts : [{ name: "", phone: "", relationship: "" }]);
  const [loading, setLoading] = useState(false);

  const toggleIntention = (i) => {
    setIntentions((p) => (p.includes(i) ? p.filter((x) => x !== i) : [...p, i]));
  };

  const updateContact = (idx, field, val) => {
    setContacts((p) => p.map((c, i) => (i === idx ? { ...c, [field]: val } : c)));
  };
  const addContact = () => setContacts((p) => [...p, { name: "", phone: "", relationship: "" }]);
  const removeContact = (idx) => setContacts((p) => p.filter((_, i) => i !== idx));

  const finish = async () => {
    setLoading(true);
    try {
      const cleanContacts = contacts.filter((c) => c.name.trim() && c.phone.trim());
      await updateProfile({
        intentions,
        trusted_contacts: cleanContacts,
        onboarded: true,
      });
      toast.success("Tīmata tātou — let's begin.");
      nav("/dashboard");
    } catch (e) {
      toast.error("Could not save — try again");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen whenua-gradient grain px-4 py-10">
      <div className="max-w-2xl mx-auto">
        <div className="flex items-center gap-2 mb-6">
          {[0, 1, 2].map((i) => (
            <div
              key={i}
              className={`h-1 flex-1 rounded-full transition-all ${i <= step ? "bg-ngahere" : "bg-border"}`}
              data-testid={`onboarding-step-${i}`}
            />
          ))}
        </div>

        <div className="bg-muka rounded-3xl p-7 sm:p-10 border border-border shadow-[0_8px_30px_rgba(42,42,42,0.06)]">
          {step === 0 && (
            <div className="animate-fadeup">
              <div className="text-[11px] uppercase tracking-[0.22em] text-kokowai mb-3">Tuatahi · first</div>
              <h1 className="font-serif text-3xl sm:text-4xl text-charcoal mb-4">
                Kia ora, {user?.display_name?.split(" ")[0]}.
              </h1>
              <p className="text-charcoal/80 leading-relaxed mb-6">
                Before we begin, a few things to know. This is <em>your</em> tool.
                No reports get sent anywhere. No probation officer is reading. Take your time —
                this onboarding can take 3 minutes or 30. The kaupapa doesn't care which.
              </p>
              <p className="text-charcoal/80 leading-relaxed mb-6">
                We work in <span className="font-medium">hybrid mode</span> — Te Whare Tapa Whā woven
                with everyday clinical wisdom. Two companion voices live here: <em>Tuahine</em> (a sister
                who has been inside) and <em>Whaea</em> (a kuia who doesn't sugar-coat). You pick.
              </p>
              <button
                onClick={() => setStep(1)}
                data-testid="onboarding-next-0"
                className="w-full sm:w-auto inline-flex items-center justify-center gap-2 px-7 py-4 rounded-full bg-ngahere text-muka hover:bg-ngahere/90 transition-colors"
              >
                Haere tonu · keep going <ArrowRight size={18} />
              </button>
            </div>
          )}

          {step === 1 && (
            <div className="animate-fadeup">
              <div className="text-[11px] uppercase tracking-[0.22em] text-kokowai mb-3">Tuarua · what matters</div>
              <h1 className="font-serif text-3xl sm:text-4xl text-charcoal mb-3">
                What are you walking towards?
              </h1>
              <p className="text-charcoal/70 mb-6">
                Pick what feels true today. You can change these any time. None of these are right or wrong.
              </p>
              <div className="flex flex-wrap gap-2 mb-8">
                {INTENTION_SUGGESTIONS.map((s) => {
                  const active = intentions.includes(s);
                  return (
                    <button
                      key={s}
                      type="button"
                      onClick={() => toggleIntention(s)}
                      data-testid={`intention-${s.toLowerCase().replace(/\s+/g, "-").replace(/[^a-z0-9-]/g, "")}`}
                      className={`px-4 py-2.5 rounded-full text-sm border transition-all ${
                        active
                          ? "bg-ngahere text-muka border-ngahere"
                          : "bg-bone text-charcoal border-border hover:border-ngahere/40"
                      }`}
                    >
                      {s}
                    </button>
                  );
                })}
              </div>
              <div className="flex justify-between">
                <button
                  onClick={() => setStep(0)}
                  data-testid="onboarding-back-1"
                  className="inline-flex items-center gap-2 px-5 py-3 rounded-full text-charcoal/70 hover:bg-bone"
                >
                  <ArrowLeft size={16} /> back
                </button>
                <button
                  onClick={() => setStep(2)}
                  data-testid="onboarding-next-1"
                  className="inline-flex items-center gap-2 px-7 py-4 rounded-full bg-ngahere text-muka hover:bg-ngahere/90 transition-colors"
                >
                  Haere tonu <ArrowRight size={18} />
                </button>
              </div>
            </div>
          )}

          {step === 2 && (
            <div className="animate-fadeup">
              <div className="text-[11px] uppercase tracking-[0.22em] text-kokowai mb-3">Tuatoru · your people</div>
              <h1 className="font-serif text-3xl sm:text-4xl text-charcoal mb-3">
                Who's your phone-call?
              </h1>
              <p className="text-charcoal/70 mb-6">
                Add at least one trusted contact. They appear behind your SOS button — one tap and you reach them.
                Skip if you need to. You can add anyone, anytime.
              </p>
              <div className="space-y-3 mb-4">
                {contacts.map((c, i) => (
                  <div key={i} className="bg-bone rounded-2xl p-4 border border-border space-y-2 relative">
                    {contacts.length > 1 && (
                      <button
                        type="button"
                        onClick={() => removeContact(i)}
                        data-testid={`contact-remove-${i}`}
                        className="absolute top-3 right-3 text-muted-foreground hover:text-kokowai"
                        aria-label="Remove contact"
                      >
                        <X size={16} />
                      </button>
                    )}
                    <input
                      placeholder="Name"
                      value={c.name}
                      onChange={(e) => updateContact(i, "name", e.target.value)}
                      data-testid={`contact-name-${i}`}
                      className="w-full px-3 py-2.5 rounded-xl bg-muka border border-border focus:outline-none focus:ring-2 focus:ring-ngahere"
                    />
                    <input
                      placeholder="Phone"
                      value={c.phone}
                      onChange={(e) => updateContact(i, "phone", e.target.value)}
                      data-testid={`contact-phone-${i}`}
                      className="w-full px-3 py-2.5 rounded-xl bg-muka border border-border focus:outline-none focus:ring-2 focus:ring-ngahere"
                    />
                    <input
                      placeholder="Relationship (sister, sponsor, mate)"
                      value={c.relationship}
                      onChange={(e) => updateContact(i, "relationship", e.target.value)}
                      data-testid={`contact-rel-${i}`}
                      className="w-full px-3 py-2.5 rounded-xl bg-muka border border-border focus:outline-none focus:ring-2 focus:ring-ngahere"
                    />
                  </div>
                ))}
              </div>
              <button
                type="button"
                onClick={addContact}
                data-testid="contact-add"
                className="inline-flex items-center gap-2 text-sm text-kokowai mb-8"
              >
                <Plus size={16} /> add another
              </button>
              <div className="flex justify-between">
                <button
                  onClick={() => setStep(1)}
                  data-testid="onboarding-back-2"
                  className="inline-flex items-center gap-2 px-5 py-3 rounded-full text-charcoal/70 hover:bg-bone"
                >
                  <ArrowLeft size={16} /> back
                </button>
                <button
                  onClick={finish}
                  disabled={loading}
                  data-testid="onboarding-finish"
                  className="inline-flex items-center gap-2 px-7 py-4 rounded-full bg-ngahere text-muka hover:bg-ngahere/90 transition-colors disabled:opacity-50"
                >
                  {loading ? "..." : "Tīmata · enter"} <ArrowRight size={18} />
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}